<template>
  <div class="formula-builder">
    <div class="formula-lines">
      <div v-for="(line, index) in formulaLines" :key="line.id" class="formula-line">
        <div class="expression">
          <span class="inline-display">
            <div v-if="index > 0" class="operator">
              <select v-model="line.operator">
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
            </div>

            <!-- Type selector now has three options -->
            <select v-model="line.type">
              <option disabled value="">Select Type</option>
              <option value="bandpass">Bandpass</option>
              <option value="const_lin">Constant (Linear)</option>
              <option value="const_log">Constant (Log)</option>
            </select>

            <!-- Bandpass UI unchanged -->
            <template v-if="line.type === 'bandpass'">
              (
              <select v-model.number="line.min">
                <option v-for="choice in getBandpassMinChoices(line)" :key="choice" :value="choice">
                  {{ choice }}
                </option>
              </select>
              ,
              <select v-model.number="line.max">
                <option v-for="choice in getBandpassMaxChoices(line)" :key="choice" :value="choice">
                  {{ choice }}
                </option>
              </select>
              )
            </template>

            <!-- Both constant types share the same input UI -->
            <template v-else-if="['const_lin', 'const_log'].includes(line.type)">
              (
              <input type="number" v-model.number="line.value" placeholder="dB" class="constant-input" />
              )
            </template>

            <!-- remove button for any filled‐in line -->
            <button @click="removeLine(index)" v-if="formulaLines.length > 1" title="Remove Line"
              class="actions-button">−</button>
          </span>
        </div>
      </div>
    </div>

    <!-- add new line -->
    <div class="add-line-bottom">
      <button @click="addLine(formulaLines.length - 1)" title="Add New Line">+</button>
    </div>

    <div v-if="overallError" class="formula-error">
      {{ overallError }}
    </div>
  </div>
</template>

<script>
export default {
  name: "FormulaBuilder",
  props: {
    backendResponse: Object,
    selectedTimestamps: Array,
    initialFormula: {
      type: String,
      default: () => 'bp(20.0,630.0)'
    },
    octaveBands: { type: Array, default: () => [] },
    interpolatedTimestamps: { type: Array, default: () => [] },
  },
  data() {
    return {
      formulaLines: [],
      nextId: 2,
      mounted: false,
    };
  },
  created() {
    const defaultMin = this.currentRange?.[0] || Math.min(...this.octaveBands, 20);
    const defaultMax = this.currentRange?.[1] || Math.max(...this.octaveBands, 630);
    this.formulaLines = [
      { id: 1, operator: '+', type: 'bandpass', min: defaultMin, max: defaultMax, value: null },
    ];
    this.emitFormulaChange();
  },
  computed: {
    timestampsToUse() {
      return this.interpolatedTimestamps.length
        ? this.interpolatedTimestamps
        : this.selectedTimestamps;
    },
    overallError() {
      const last = this.formulaLines.at(-1);
      if (!last || !last.type) return "Formula incomplete or ends with operator!";
      if (last.type === 'bandpass' && (last.min == null || last.max == null))
        return "Incomplete bandpass values.";
      if (['const_lin', 'const_log'].includes(last.type) && last.value == null)
        return "Constant value missing.";
      return null;
    },
    results() {
      const out = [];
      if (!this.backendResponse?.third_octaves) {
        return this.timestampsToUse.map(ts => ({
          timestamp: ts, value: null, error: "Missing data."
        }));
      }

      for (const ts of this.timestampsToUse) {
        let error = null;
        let bandpassSum = null;

        // 1) process bp(...) and const_log(...) as linear sums
        for (const line of this.formulaLines) {
          if (line.type === 'bandpass' || line.type === 'const_log') {
            let linearTerm = 0;
            let found = false;

            if (line.type === 'bandpass') {
              // sum all matching third-octave bands
              for (const [band, data] of Object.entries(this.backendResponse.third_octaves)) {
                const freq = this.parseToHz(band);
                if (freq >= line.min && freq <= line.max && ts in data) {
                  linearTerm += Math.pow(10, data[ts] / 10);
                  found = true;
                }
              }
              if (!found) {
                error = `No bands in ${line.min}-${line.max}`;
                break;
              }
            } else {
              // const_log: treat constant dB as linear power
              linearTerm = Math.pow(10, line.value / 10);
              found = true;
            }

            // accumulate into bandpassSum
            bandpassSum = bandpassSum === null
              ? linearTerm
              : (line.operator === '+'
                ? bandpassSum + linearTerm
                : bandpassSum - linearTerm);
          }
        }

        // 2) process const_lin lines as pure dB offsets
        let constantOffset = 0;
        if (!error) {
          for (const line of this.formulaLines.filter(l => l.type === 'const_lin')) {
            if (line.value == null) {
              error = "Constant value missing.";
              break;
            }
            constantOffset = line.operator === '+'
              ? constantOffset + line.value
              : constantOffset - line.value;
          }
        }

        // 3) finalize value
        if (!error && bandpassSum < 0) {
          error = "Non-positive power sum.";
        }
        const linearBand = bandpassSum === null ? 0 : 10 * Math.log10(bandpassSum);
        const value = error ? null : linearBand + constantOffset;
        out.push({ timestamp: ts, value, error });
      }

      return out;
    },
  },
  methods: {
    addLine(index) {
      this.formulaLines.splice(index + 1, 0, {
        id: this.nextId++,
        operator: '+',
        type: '',
        min: null,
        max: null,
        value: null
      });
    },
    parseToHz(str) {
      const m = str.match(
        /^\s*([+-]?\d*\.?\d+(?:[eE][+-]?\d+)?)\s*(?:([kK]?)[Hh][Zz])?\s*$/
      );
      if (!m) return NaN;
      let value = parseFloat(m[1]);
      if (m[2] && m[2].toLowerCase() === 'k') {
        value *= 1000;
      }
      return value;
    },
    removeLine(index) {
      if (this.formulaLines.length > 1) this.formulaLines.splice(index, 1);
    },
    getBandpassMinChoices(line) {
      const max = line.max ?? Math.max(...this.octaveBands);
      return this.octaveBands.filter(b => b <= max);
    },
    getBandpassMaxChoices(line) {
      const min = line.min ?? Math.min(...this.octaveBands);
      return this.octaveBands.filter(b => b >= min);
    },
    buildFormulaRegex() {
      return this.formulaLines.map((line, i) => {
        let term = '';
        if (line.type === 'bandpass') {
          term = `bp(${line.min},${line.max})`;
        } else if (line.type === 'const_lin') {
          term = `CONST_LIN(${line.value})`;
        } else if (line.type === 'const_log') {
          term = `CONST_LOG(${line.value})`;
        }
        return i === 0 ? term : `${line.operator}${term}`;
      }).join('');
    },
    parseTerm(token, operator) {
      const bpPattern = /^bp\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)$/i;
      const constPattern = /^CONST_(LIN|LOG)\(\s*(\d+(?:\.\d+)?)\s*\)$/i;

      let m = token.match(bpPattern);
      if (m) {
        return {
          operator,
          type: "bandpass",
          min: parseFloat(m[1]),
          max: parseFloat(m[2]),
          value: null
        };
      }
      m = token.match(constPattern);
      if (m) {
        return {
          operator,
          type: m[1].toLowerCase() === 'lin' ? 'const_lin' : 'const_log',
          min: null,
          max: null,
          value: parseFloat(m[2])
        };
      }
      throw new Error("Invalid term token: " + token);
    },
    parseFormula(formula) {
      // now accepts bp(...), CONST_LIN(...) or CONST_LOG(...)
      const formulaPattern = /^(?:bp\(\s*\d+(?:\.\d+)?\s*,\s*\d+(?:\.\d+)?\s*\)|CONST_(?:LIN|LOG)\(\s*\d+(?:\.\d+)?\s*\))(?:\s*[+-]\s*(?:bp\(\s*\d+(?:\.\d+)?\s*,\s*\d+(?:\.\d+)?\s*\)|CONST_(?:LIN|LOG)\(\s*\d+(?:\.\d+)?\s*\)))*$/i;
      if (!formulaPattern.test(formula)) {
        throw new Error("Invalid formula format");
      }
      const tokenPattern = /bp\(\s*\d+(?:\.\d+)?\s*,\s*\d+(?:\.\d+)?\s*\)|CONST_(?:LIN|LOG)\(\s*\d+(?:\.\d+)?\s*\)|[+-]/gi;
      const tokens = formula.match(tokenPattern);
      const lines = [];
      let i = 0;
      // first term default '+'
      if (tokens && tokens.length) {
        lines.push(this.parseTerm(tokens[0], '+'));
        i = 1;
      }
      while (tokens && i < tokens.length) {
        const op = tokens[i].trim(); i++;
        if (i >= tokens.length) {
          throw new Error("Operator without following term");
        }
        lines.push(this.parseTerm(tokens[i], op));
        i++;
      }
      if (lines.length) this.formulaLines = lines;
    },
    emitFormulaChange() {
      if (!this.mounted || this.overallError) return;
      this.$emit(
        "formulaChanged",
        this.results.map(r => r.value),
        this.results.find(r => r.error)?.error || null,
        this.buildFormulaRegex()
      );
    },
  },
  mounted() {
    this.parseFormula(this.initialFormula);
    this.mounted = true;
  },
  watch: {
    formulaLines: {
      handler: "emitFormulaChange",
      deep: true
    },
    selectedTimestamps: "emitFormulaChange",
    interpolatedTimestamps: "emitFormulaChange",
    backendResponse: "emitFormulaChange"
  }
};
</script>

<style scoped>
.formula-builder {
  border: 1px solid var(--colour-border);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-top: var(--spacing-md);
  background: #fafafa;
}

.formula-lines {
  /* Ensures that the group of formula lines is styled properly */
}

.formula-line {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;
}

.operator select,
.expression select,
.bandpass-inputs select,
.constant-input {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  border: 1px solid var(--colour-border);
}

.constant-input {
  width: 5rem;
}

.inline-display {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.actions-button {
  font-weight: bold;
  width: var(--icon-size-lg);
  height: var(--icon-size-lg);
  border-radius: 50%;
  border: none;
  background-color: #e0e0e0;
  cursor: pointer;
  margin-left: 0;
  transition: background-color 0.2s ease;
}

.actions-button:hover {
  background-color: #d0d0d0;
}

/* Styling for the single bottom adder */
.add-line-bottom {
  margin-top: var(--spacing-md);
  text-align: center;
}

.add-line-bottom button {
  font-weight: bold;
  width: var(--icon-size-lg);
  height: var(--icon-size-lg);
  border-radius: 50%;
  border: none;
  background-color: #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.add-line-bottom button:hover {
  background-color: #d0d0d0;
}

.formula-error {
  color: #d32f2f;
  margin-top: var(--spacing-md);
  font-weight: 500;
}
</style>
